import { MouseEvent, useRef, useState } from "react";
import Badge from "@/app/(components)/generics/Badge/Badge";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import {
  useBrand,
  useCanManageCompany,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { StandardCallType } from "@/app/types/call/StandardCallType";
import { PageParams } from "@/app/types/common/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import styles from "./TypeSelector.module.css";

type Props = {
  value: string | null;
  onChange: (value: string | null) => void;
  params: PageParams;
  disabled?: boolean;
  suggestions?: string[];
};

export default function TypeSelector(props: Props) {
  const T = useT();
  const { value, onChange, params, disabled = false, suggestions = [] } = props;

  const projectId = ProjectStore((state) => state.currentProject?.id);
  const isTenant = useBrand() !== null;
  const canManageCompany = useCanManageCompany();
  // Project call types are a project-level setting: only managers may create/edit/delete them.
  const canManageTypes = !isTenant || canManageCompany;

  const [isOpen, setIsOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const standardTypes = Object.values(StandardCallType);
  const allSuggestions = Array.from(new Set([...suggestions, ...standardTypes])).slice(0, 10);

  const isStandard = (type: string) => standardTypes.includes(type as StandardCallType);

  const customCount = Array.from(new Set(suggestions.filter((type) => !isStandard(type)))).length;
  const atLimit = customCount >= Limits.MAX_PROJECT_CUSTOM_CALL_TYPES;

  const handleAddCustom = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
      setCustomValue("");
      setIsOpen(false);
    }
  };

  const handleSelect = (type: string) => {
    onChange(type);
    setIsOpen(false);
  };

  const handleEdit = (e: MouseEvent, type: string) => {
    e.stopPropagation();
    if (!projectId) {
      return;
    }
    setIsOpen(false);
    ModalStore.getState().openEditCallType(params, projectId, type, (newName) => {
      if (value === type) {
        onChange(newName);
      }
    });
  };

  const handleDelete = (e: MouseEvent, type: string) => {
    e.stopPropagation();
    if (!projectId) {
      return;
    }
    setIsOpen(false);
    ModalStore.getState().openDeleteCallType(params, projectId, type, (deleted) => {
      if (value === deleted) {
        onChange(null);
      }
    });
  };

  const getBadgeClass = (type: string | null) => {
    if (!type) return styles.typeOther;
    if (type === StandardCallType.DISCOVERY) return styles.typeDiscovery;
    if (type === StandardCallType.DEMO) return styles.typeDemo;
    if (type === StandardCallType.INTERVIEW) return styles.typeInterview;
    if (type === StandardCallType.CLOSING) return styles.typeClosing;
    return styles.typeOther;
  };

  if (disabled) {
    return (
      <div className={styles.container}>
        <Badge className={UStyles.classNames(styles.trigger, getBadgeClass(value))}>
          <span className={styles.value}>
            {value ? (isStandard(value) ? T(`CALL_TYPE_${value}` as TKey) : value) : ""}
          </span>
        </Badge>
      </div>
    );
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <Popover
        isVisible={isOpen}
        setIsVisible={setIsOpen}
        openAfterClick={true}
        closeWhenClickOutside={true}
        isPortaled={true}
      >
        <PopoverTrigger>
          <Badge className={UStyles.classNames(styles.trigger, getBadgeClass(value))}>
            <span className={styles.value}>
              {value ? (isStandard(value) ? T(`CALL_TYPE_${value}` as TKey) : value) : ""}
            </span>
            <div className={styles.arrowContainer}>
              {isOpen ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
            </div>
          </Badge>
        </PopoverTrigger>

        <PopoverContent
          position={{
            default: { top: "8px", left: "0px" },
          }}
        >
          <div className={styles.popoverContent}>
            <div className={styles.list}>
              {allSuggestions.map((type) => (
                <div
                  key={type}
                  className={UStyles.classNames(styles.item, value === type && styles.active)}
                  onClick={() => handleSelect(type)}
                >
                  <span className={styles.itemLabel}>
                    {isStandard(type) ? T(`CALL_TYPE_${type}` as TKey) : type}
                  </span>
                  {!isStandard(type) && canManageTypes && (
                    <div className={styles.itemActions}>
                      <Button
                        isLink={false}
                        type={ButtonType.GHOST}
                        iconOnly={true}
                        min={true}
                        className={styles.actionBtn}
                        beforeIcon={<IconEdit width={15} height={15} />}
                        onClick={(e: MouseEvent) => handleEdit(e, type)}
                        style={{ color: "var(--color-grey-6)" }}
                      />
                      <Button
                        isLink={false}
                        type={ButtonType.GHOST}
                        iconOnly={true}
                        min={true}
                        className={styles.actionBtn}
                        beforeIcon={<IconTrash size={15} />}
                        onClick={(e: MouseEvent) => handleDelete(e, type)}
                        style={{ color: "var(--color-red-1)" }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!atLimit && canManageTypes && (
              <div className={styles.footer}>
                <input
                  className={styles.input}
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  placeholder={T(TKey.CALL_TYPE_OTHER)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddCustom()}
                  onClick={(e) => e.stopPropagation()}
                />
                <button className={styles.addBtn} onClick={handleAddCustom}>
                  <IconPlus />
                </button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
